import { assignPackageItems } from '../../../utility/packaging/package-items';
import styled from 'styled-components';
import { Barcode, Cube, Hand, Package, Ruler, Scales } from '@phosphor-icons/react';
import { useMemo } from 'react';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider, useDrop } from 'react-dnd';
import {
  ColoredPill,
  HeaderText,
  MuteBorder,
  MuteText,
  ShadowBox,
} from '../../../elements';
import { LoaderBar } from '../../../components';
import { CommonUtility, theme } from '../../../utility';
import { ItemTypes } from '../../zones/common';
import { DraggableItem, ReadOnlyItem } from './item';
import { PackageLabel } from './generate-pack-labels';
import { ConversionUtility } from '../../../utility/conversions';
import { sumContentsWeightInPounds } from '../../../utility/packaging/packed-weight';

const StyledShadowBox = styled(ShadowBox)`
  flex: 1;
  margin-left: 0px !important;

  &.is-active {
    border: 2px solid ${({ theme }) => theme.colors.green};
  }
`;

const ItemsContainer = styled.div`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  row-gap: 10px;
  &.is-over {
    background: ${({ theme }) => theme.colors.box};
  }
  border-radius: 5px;
`;

const PlaceholderContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  &.is-over {
    background: ${({ theme }) => theme.colors.box};
  }
  border-radius: 5px;
`;

function PackRenderer({
  activate,
  allItemsPacked,
  increaseCount,
  index,
  isActive,
  loading,
  packedItems,
  unPackItem,
  readOnly,
  pack,
}) {
  const [{ isOver, canDrop, handlerId }, itemsRef] = useDrop(() => ({
    accept: ItemTypes.packageItem,
    drop: item => increaseCount(item, index),
    collect: monitor => ({
      isOver: !!monitor.isOver(),
      canDrop: !!monitor.canDrop(),
      handlerId: monitor.getHandlerId(),
    }),
    canDrop: item => {
      if (item.packageIndex === index) {
        return false;
      }
      return true;
    },
  }));

  const totalWeight = useMemo(() => {
    if (!CommonUtility.isValidArray(packedItems)) return;

    const weight = sumContentsWeightInPounds(
      packedItems.map(packItem => ({
        weight: packItem.productId.package_dimensions?.weight,
      })),
    );

    if (!weight) {
      return null;
    }

    return ConversionUtility.convertToPoundsAndOunces(weight);
  }, [packedItems]);

  return (
    <StyledShadowBox className={`${isActive ? 'is-active' : ''} ml-1 d-flex flex-column`}>
      {loading && <LoaderBar />}

      <div onClick={activate} className="d-flex justify-content-between cursor-pointer">
        <div className="d-flex align-items-center">
          <Package size={24} color={theme.colors.grey} />
          <HeaderText className="mx-1">Package {index + 1}</HeaderText>
        </div>
        <div className="d-flex align-items-center">
          <Scales color={theme.colors.grey} size={20} />
          {CommonUtility.isValidObject(totalWeight) ? (
            <span className="ml-2">
              {CommonUtility.roundNumber(totalWeight?.pounds || 0)}
              <MuteText className="ml-1 mr-2">lb</MuteText>
              {CommonUtility.roundNumber(totalWeight?.ounces || 0)}
              <MuteText className="ml-1">oz</MuteText>
            </span>
          ) : (
            <MuteText className="ml-2">Not Set</MuteText>
          )}
        </div>
      </div>
      {CommonUtility.isValidArray(packedItems) ? (
        <>
          <ItemsContainer
            ref={itemsRef}
            data-handler-id={handlerId}
            className={`${canDrop && isOver ? 'is-over' : ''}`}
          >
            {packedItems?.map((item, itemIndex) => (
              <>
                {readOnly ? (
                  <ReadOnlyItem item={item} />
                ) : (
                  <DraggableItem
                    item={item}
                    unPackItem={unPackItem}
                    packageIndex={index}
                    readOnly={readOnly}
                  />
                )}
                {itemIndex !== packedItems?.length - 1 && <MuteBorder />}
              </>
            ))}
          </ItemsContainer>
          {pack.label?.packagingMaterial?.name && (
            <div className="d-flex mt-4">
              <ColoredPill>
                <Cube />
                <span>{packedItems.length} items</span>
              </ColoredPill>
              <ColoredPill className="mx-4">
                <Ruler />
                <div className="d-flex">
                  <span>
                    {CommonUtility.roundNumber(
                      pack.label.packagingMaterial?.dimensions?.length,
                    )}
                  </span>
                  <span className="mx-1">x</span>
                  <span>
                    {CommonUtility.roundNumber(
                      pack.label.packagingMaterial?.dimensions?.width,
                    )}
                  </span>
                  <span className="mx-1">x</span>
                  <span>
                    {CommonUtility.roundNumber(
                      pack.label.packagingMaterial?.dimensions?.height,
                    )}
                  </span>
                  <span className="mx-1">
                    ({pack.label.packagingMaterial?.dimensions?.uom})
                  </span>
                </div>
              </ColoredPill>
              <ColoredPill>
                <Package />
                <span>{pack.label.packagingMaterial.name}</span>
              </ColoredPill>
            </div>
          )}
        </>
      ) : (
        <PlaceholderContainer
          ref={itemsRef}
          data-handler-id={handlerId}
          className={`${canDrop && isOver ? 'is-over' : ''}`}
        >
          <div className="d-flex flex-column align-items-center">
            {allItemsPacked ? (
              <>
                <Hand size={36} color={theme.colors.grey} />
                <HeaderText>Drag Items Here</HeaderText>
              </>
            ) : (
              <>
                <Barcode size={36} color={theme.colors.grey} />
                <HeaderText>Scan an Item to Pack</HeaderText>
              </>
            )}
          </div>
        </PlaceholderContainer>
      )}
    </StyledShadowBox>
  );
}

const PackagesContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 10px;
  height: 100%;
  .pack {
    flex: 1;
  }
`;

export function MultiplePacks({
  activePackage,
  allItemsPacked,
  askToPrint,
  fileName,
  hasShipNode,
  increaseCount,
  items,
  loading,
  openLabelGenerationForPackage,
  onOpenPackageCorrection,
  packages,
  setActivePackage,
  setLabelToRefund,
  unPackItem,
  statusToCheck = 'packed',
  readOnly = false,
}) {
  const populatedPackages = useMemo(() => {
    const packed = items.filter(x => x.status === statusToCheck && !x.isCancelled);
    return assignPackageItems(packages, packed).packages;
  }, [packages, items, statusToCheck]);

  return (
    <DndProvider backend={HTML5Backend}>
      <PackagesContainer>
        {populatedPackages.map((pack, i) => (
          <div className="row pack">
            <PackRenderer
              activate={() => setActivePackage(i)}
              increaseCount={increaseCount}
              index={i}
              isActive={activePackage === i}
              loading={loading}
              pack={pack}
              packedItems={pack.packedItems}
              unPackItem={item => unPackItem(item, i)}
              readOnly={readOnly}
              allItemsPacked={allItemsPacked}
            />
            {allItemsPacked && (
              <PackageLabel
                readOnly={readOnly}
                askToPrint={askToPrint}
                fileName={fileName}
                hasShipNode={hasShipNode}
                index={i}
                onOpenPackageCorrection={onOpenPackageCorrection}
                openLabelGenerationForPackage={() => openLabelGenerationForPackage(i)}
                pack={pack}
                setLabelToRefund={setLabelToRefund}
              />
            )}
          </div>
        ))}
      </PackagesContainer>
    </DndProvider>
  );
}
